import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/layout';
import SEO from '../components/seo';

import { Link } from 'gatsby';
import { rawContentToPlainText, slugify } from '../utils/utils';
import '../styles/venue.scss';
import ImageCarousel from '../components/image-carousel';
import '../images/tick-pink.svg';
import VenueConfigurations from "../components/venue-configurations";
import PricingTable from '../components/pricing-table';
import VenueBox from "../components/venue-box";
import WoolwichLogo from "../components/woolwich-logo";
import Img from "gatsby-image";
import BodyText from '../components/woolwich-portable-text';
export const query = graphql`
  query VenueTemplateQuery($id: String!) {
    site: sanitySiteSettings(_id: {regex: "/(drafts.|)siteSettings/"}) {
      title
      description
    }
    venue: sanityVenue(id: {eq: $id}) {
      people
      sqft
      dimensions
      title
      imageSlider{
        asset {
          fluid(maxWidth: 1200) {
          ...GatsbySanityImageFluid
          }
          url
        }
        attribution
      }
      video
      # story{
      #     title
      # }
      configurationItems{
          _rawContent
           asset{
               ... on SanityImage {
                asset {
                  url
                   fluid(maxWidth: 1200) {
                     ...GatsbySanityImageFluid
                   }
                }
              }
              ... on SanityExtendedUrl {
                url
              }
           }
           navHeading
           title
      }
      pricing {
        rows {
          cells
        }
      }
      contactName
      contactEmail
      contactEmailDisplayText
      contactNumber
      descriptionTitle
      _rawParagraph
      technicalSpec {
        asset {
          url
        }
      }
      technicalSpecLinkText
      suitableForTitle
      suitableForTags {
        title
      }
      facilitiesTags {
        title
      }
      relatedVenues {
        people
        sqft
        title
        suitableForTags{
          title
        }
        imageSlider {
          asset {
            fluid(maxWidth: 1200) {
              ...GatsbySanityImageFluid
            }
          }
          attribution
        }    
      }
    }
    venueHire: sanityVenueHire {
      title
    }
  }
`;

const EnquireButton = ({venue}) => <a className='button venue__button' href={`mailto:${venue.contactEmail}`}>{venue.contactEmailDisplayText || `Email ${venue.contactName}`}&nbsp;→</a>

const VenueTemplate = ({ location, data }) => {
  const venue = data && data.venue;

  return (
    <Layout pageTitle={venue.title} pageTitleColour={'regal-purple'} breadcrumb={[
      {
        title: data.venueHire.title,
        link: `/venue-hire`
      }
    ]}>
      <SEO
        location={location}
        title={`${venue.title} | ${data.site.title}`}
        description={venue._rawParagraph !== null ? rawContentToPlainText(venue._rawParagraph).substring(0, 120) + '...' : null}
        imageAsset={venue.imageSlider[0].asset}
      />

      {/* Carousel */}
      <div className='image-carousel-harness'>
        <ImageCarousel images={venue.imageSlider} video={venue.video} />
      </div>

      <div className='print-style-logo'>
        <WoolwichLogo classes='header__logo' />
      </div>

      <div className='print-style-title'>{venue.title}</div>
      <Img className='print-style-main-image' fluid={venue.imageSlider[0].asset.fluid} />

      <div className='strip strip--pad-top-small'>
        <div className='strip__middle pad-sides'>

          {/* Info boxes */}
          <div className='venue__info-boxes-harness'>
            <div className='venue-info-box'>
              <div className='venue-info-box__title'>Space</div>
              <div className='venue__top-line-info'>{venue.sqft}m<sup>2</sup></div>
              <div className='venue__top-line-info'>Up to {venue.people} people</div>
              <div className='venue__top-line-info'>{venue.dimensions}</div>
            </div>

            <div className='venue-info-box'>
              <div className='venue-info-box__title'>Facilities</div>
              {
                venue.facilitiesTags.map((tag, idx) => {
                  return <div key={idx} className='venue__top-line-info'>{tag.title}</div>
                })
              }
            </div>

            <div className='venue-info-box'>
              <div className='venue-info-box__title'>{venue.suitableForTitle || 'Suitable for'}</div>
              {
                venue.suitableForTags.map((tag, idx) => {
                  return <div key={idx} className='venue__top-line-info'>{tag.title}</div>
                })
              }
            </div>
          </div>

          {/* Description */}
          <div className='venue__description'>
            <div className='venue__description-title'>{venue.descriptionTitle}</div>
            <BodyText
              serializers={{ types: {} }}
              blocks={venue._rawParagraph}
            />
          </div>

          {/* {
            venue.story &&
            <div className='venue__story'>
              <Link className='button venue__button' to={'/story/' + slugify(venue.story.title)}>{venue.story.title} ↗</Link>
            </div>
          } */}

          {
            venue.configurationItems.length > 0 &&
            <EnquireButton venue={venue} />
          }

          {/* Images block for print */}
          <div className='print-style-images'>
            {
              venue.imageSlider.map((image, idx) => {
                if (idx > 0) return <Img className='print-style-images__image' fluid={image.asset.fluid} />;
                else return <></>;
              })              
            }
          </div>

          {/* Configurations */}
          <VenueConfigurations items={venue.configurationItems} />

          <div className="venue-configurations__printonly">
            {
              venue.configurationItems.map((item, idx) => {
                return <div key={idx} className='configuration-item__item'>
                  <div className='venue-configurations__tab-title'>{item.title}</div>
                  <BodyText blocks={item._rawContent} />
                  <div className='venue-configurations__tab-pane venue-configurations__tab-pane--img'>
                    <Img fluid={item.asset[0].asset.fluid} />
                  </div>
                </div>
              })


            }
          </div>

          {/* Pricing table */}
          <PricingTable pricing={venue.pricing} />

          {/* Misc Info */}
          <div className='venue__info-boxes-harness venue__info-boxes-harness--contact'>
            <div className='venue-info-box'>
              {/* <div className='venue-contact__title'>Contact</div> */}
              <EnquireButton venue={venue} />
              {
                venue.contactNumber &&
                <a className='venue-contact__link' href={`tel:${venue.contactNumber}`}>{venue.contactNumber}</a>
              }              
            </div>

            <div className='venue-info-box tech-spec-print'>
              <div className='venue-contact__title'>Additional Information</div>
              {
                venue.technicalSpec &&
                <a className='venue-contact__link'
                  href={`${venue.technicalSpec.asset.url}?dl=${slugify(venue.title)}.pdf`} download>{venue.technicalSpecLinkText || 'Technical Specification PDF'}</a>
              }
              <a className='venue-contact__link' href='#' onClick={typeof window !== `undefined` ? window.print : () => { }}>Print this page</a>
            </div>
          </div>
        </div>
      </div>
      {
        !!venue.relatedVenues.length &&
        <div className='strip strip--beige venue__similar-venues'>
          <div className='strip__middle pad-sides'>
            <div className='venue__similar-venues-title'>Similar Venues</div>
            <div className='venue__similar-venues-box-list'>
              {
                venue.relatedVenues && venue.relatedVenues.map((venue, idx) => {
                  return (
                    <VenueBox data={venue} key={idx} />
                  );
                }
                )
              }
            </div>
          </div>
        </div>
      }

    </Layout>
  );
}

export default VenueTemplate;

